(() => {
  'use strict';

  const ROOT_ID = 'zv-cookie-consent';
  const PREVIEW_PARAM = 'zv_cookie_preview';
  const PREFERENCES_PARAM = 'zv_cookie_preferences';
  const API_TIMEOUT = 8000;

  function clearPreviewParameters() {
    const url = new URL(window.location.href);
    const hadPreviewParameter = url.searchParams.has(PREVIEW_PARAM) || url.searchParams.has(PREFERENCES_PARAM);

    if (!hadPreviewParameter) return;

    url.searchParams.delete(PREVIEW_PARAM);
    url.searchParams.delete(PREFERENCES_PARAM);
    window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
  }

  let root;
  let api;
  let activePanel;
  let previousFocus = null;
  let openedFromFooter = false;
  let hasRecordedChoice = false;

  const selectors = {
    intro: '[data-zv-cookie-panel="intro"]',
    preferences: '[data-zv-cookie-panel="preferences"]',
    more: '[data-zv-cookie-more]',
    acceptAll: '[data-zv-cookie-accept-all]',
    save: '[data-zv-cookie-save]',
    analytics: '[data-zv-cookie-analytics]',
    marketing: '[data-zv-cookie-marketing]',
    personalization: '[data-zv-cookie-personalization]',
    status: '[data-zv-cookie-status]'
  };

  const getPanel = (name) => root?.querySelector(selectors[name]);
  const getCurrentConsent = () => {
    try {
      return api?.currentVisitorConsent?.() || {};
    } catch (error) {
      console.warn('[ZinVolt cookies] Kon huidige toestemming niet lezen.', error);
      return {};
    }
  };

  function loadPrivacyApi() {
    return new Promise((resolve, reject) => {
      const startedAt = Date.now();

      function attempt() {
        if (window.Shopify?.customerPrivacy) {
          resolve(window.Shopify.customerPrivacy);
          return;
        }

        if (window.Shopify?.loadFeatures) {
          window.Shopify.loadFeatures(
            [{ name: 'consent-tracking-api', version: '0.1' }],
            (error) => {
              if (error) {
                reject(error);
                return;
              }

              if (window.Shopify?.customerPrivacy) {
                resolve(window.Shopify.customerPrivacy);
              } else {
                reject(new Error('Shopify Customer Privacy API is niet beschikbaar.'));
              }
            }
          );
          return;
        }

        if (Date.now() - startedAt >= API_TIMEOUT) {
          reject(new Error('Shopify Customer Privacy API kon niet worden geladen.'));
          return;
        }

        window.setTimeout(attempt, 100);
      }

      attempt();
    });
  }

  function hasExistingChoice(consent) {
    return ['analytics', 'marketing', 'preferences'].some(
      (key) => consent?.[key] === 'yes' || consent?.[key] === 'no'
    );
  }

  function syncPreferenceControls(defaultOn = false) {
    const consent = getCurrentConsent();
    const analytics = root.querySelector(selectors.analytics);
    const marketing = root.querySelector(selectors.marketing);
    const personalization = root.querySelector(selectors.personalization);
    const recordedChoice = hasExistingChoice(consent);

    hasRecordedChoice = recordedChoice;

    if (analytics) {
      analytics.checked = recordedChoice
        ? consent.analytics === 'yes' || consent.preferences === 'yes'
        : defaultOn;
    }

    if (marketing) {
      marketing.checked = recordedChoice
        ? consent.marketing === 'yes'
        : defaultOn;
    }

    if (personalization) {
      personalization.checked = recordedChoice
        ? consent.preferences === 'yes'
        : defaultOn;
    }
  }

  function getFocusable(container) {
    return Array.from(
      container.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => !element.hasAttribute('hidden') && element.offsetParent !== null);
  }

  function setPanel(name, options = {}) {
    const intro = getPanel('intro');
    const preferences = getPanel('preferences');

    intro.hidden = name !== 'intro';
    preferences.hidden = name !== 'preferences';
    activePanel = name === 'intro' ? intro : preferences;

    if (name === 'preferences') {
      syncPreferenceControls(Boolean(options.defaultOn));
    }

    window.requestAnimationFrame(() => {
      activePanel?.focus({ preventScroll: true });
    });
  }

  function openConsent(panelName = 'intro', fromFooter = false, defaultOn = false) {
    if (!root) return;

    previousFocus = document.activeElement;
    openedFromFooter = fromFooter;
    root.hidden = false;
    document.documentElement.classList.add('zv-cookie-lock');
    setPanel(panelName, { defaultOn });
  }

  function closeConsent() {
    if (!root) return;

    root.hidden = true;
    getPanel('intro').hidden = true;
    getPanel('preferences').hidden = true;
    document.documentElement.classList.remove('zv-cookie-lock');
    activePanel = null;

    if (previousFocus && typeof previousFocus.focus === 'function') {
      previousFocus.focus({ preventScroll: true });
    }
  }

  function setSaving(isSaving) {
    const buttons = root.querySelectorAll(`${selectors.acceptAll}, ${selectors.save}`);
    buttons.forEach((button) => {
      button.disabled = isSaving;
      if (!button.dataset.originalText) button.dataset.originalText = button.textContent.trim();
      button.textContent = isSaving ? 'Even opslaan…' : button.dataset.originalText;
    });
  }

  function setStatus(message = '') {
    const status = root.querySelector(selectors.status);
    if (status) status.textContent = message;
  }

  function saveConsent(values) {
    if (!api?.setTrackingConsent) {
      setStatus('De cookievoorkeuren konden nog niet worden opgeslagen. Vernieuw de pagina en probeer het opnieuw.');
      return;
    }

    setSaving(true);
    setStatus('');

    try {
      api.setTrackingConsent(values, () => {
        setSaving(false);
        hasRecordedChoice = true;
        clearPreviewParameters();
        document.dispatchEvent(
          new CustomEvent('zv:cookie-consent-updated', { detail: values })
        );
        closeConsent();
      });
    } catch (error) {
      console.error('[ZinVolt cookies] Opslaan mislukt.', error);
      setSaving(false);
      setStatus('Opslaan is niet gelukt. Probeer het opnieuw.');
    }
  }

  function injectFooterLink() {
    if (document.getElementById('zv-cookie-footer-link')) return;

    const button = document.createElement('button');
    button.id = 'zv-cookie-footer-link';
    button.type = 'button';
    button.className = 'zv-cookie-footer-link';
    button.textContent = 'Cookievoorkeuren aanpassen';
    button.setAttribute('data-zv-cookie-open', '');

    const policyList = document.querySelector('.footer__policies');
    if (policyList) {
      const item = document.createElement('li');
      item.className = 'zv-cookie-footer-item';
      item.appendChild(button);
      policyList.appendChild(item);
      return;
    }

    const copyright = document.querySelector('.footer__copyright');
    if (copyright) {
      copyright.appendChild(button);
      return;
    }

    const footer = document.querySelector('footer');
    footer?.appendChild(button);
  }

  function bindEvents() {
    root.querySelector(selectors.more)?.addEventListener('click', () => {
      setPanel('preferences', { defaultOn: true });
    });

    root.querySelector(selectors.acceptAll)?.addEventListener('click', () => {
      saveConsent({ analytics: true, marketing: true, preferences: true });
    });

    root.querySelector(selectors.save)?.addEventListener('click', () => {
      const analytics = Boolean(root.querySelector(selectors.analytics)?.checked);
      const marketing = Boolean(root.querySelector(selectors.marketing)?.checked);
      const personalization = Boolean(root.querySelector(selectors.personalization)?.checked);

      saveConsent({
        analytics,
        marketing,
        preferences: personalization
      });
    });

    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-zv-cookie-open]');
      if (!trigger) return;
      event.preventDefault();
      openConsent('preferences', true);
    });

    document.addEventListener('keydown', (event) => {
      if (root.hidden || !activePanel) return;

      if (event.key === 'Escape' && openedFromFooter && hasRecordedChoice) {
        closeConsent();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = getFocusable(activePanel);
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    document.addEventListener('shopify:section:load', injectFooterLink);
  }

  async function init() {
    root = document.getElementById(ROOT_ID);
    if (!root) return;

    bindEvents();
    injectFooterLink();

    const params = new URLSearchParams(window.location.search);
    const forcePreview = params.get(PREVIEW_PARAM) === '1';
    const forcePreferences = params.get(PREFERENCES_PARAM) === '1';

    if (forcePreview || forcePreferences) {
      clearPreviewParameters();
    }

    try {
      api = await loadPrivacyApi();
      const consent = getCurrentConsent();
      hasRecordedChoice = hasExistingChoice(consent);

      if (forcePreferences) {
        openConsent('preferences', true, true);
      } else if (forcePreview) {
        openConsent('intro');
      } else if (!hasRecordedChoice && api.shouldShowBanner?.()) {
        openConsent('intro');
      }
    } catch (error) {
      console.error('[ZinVolt cookies] Initialisatie mislukt.', error);

      if (forcePreferences || forcePreview) {
        openConsent(forcePreferences ? 'preferences' : 'intro', forcePreferences);
        setStatus('Voorbeeldmodus: de Shopify privacykoppeling is nog niet beschikbaar.');
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
